import { Link } from '@inertiajs/react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { BusinessSidebar, BusinessMenuButton } from '@/components/layout/BusinessSidebar'
import { MilestoneTrack, type TrackStep } from '@/components/businesses/MilestoneTrack'
import { Button } from '@/components/ui/button'
import { Page, PageHeader, Section, Rows, Row, Empty } from '@/components/page'
import { Tip } from '@/components/sketch/Tip'
import { useBasePath } from '@/lib/paths'
import { formatCurrency } from '@/lib/utils'
import { BusinessDetailed, BusinessOrderState, BusinessOrderSummary, BusinessTaskDocument, FormationTracker } from '@/types'
import { Building2, ClipboardList, CreditCard, Download, FileText, Package } from 'lucide-react'
import { ListStatus, type StatusTone } from '@/components/ui/list'
import { MILESTONE_LABELS, describeMilestone } from '../Formations/components/MilestoneList'
import { CertificateCard } from '../Formations/components/CertificateCard'

type BusinessTaskRef = {
  id: string
  type_name: string
  status: 'pending' | 'responded' | 'completed'
  due_date: string | null
  overdue: boolean
}

type Props = {
  business: Omit<BusinessDetailed, 'email' | 'inbound_emails_count'>
  workspace_section: 'timeline' | 'documents' | 'billing'
  formation_tracker: FormationTracker | null
  tasks: BusinessTaskRef[]
  orders: BusinessOrderSummary[]
  task_documents: { documents: BusinessTaskDocument[]; templates: BusinessTaskDocument[] }
}

// Customer order labels, serialized server-side from records — setup/order
// status only, never a regulatory filing outcome.
const ORDER_STATE_LABELS: Record<BusinessOrderState, { label: string; tone: StatusTone }> = {
  unpaid: { label: 'Payment needed', tone: 'idle' },
  refunded: { label: 'Refunded', tone: 'idle' },
  ready: { label: 'Order received', tone: 'progress' },
  in_progress: { label: 'With Corppy', tone: 'progress' },
  completed: { label: 'Setup complete', tone: 'positive' },
}

// An order's stage, read from its records — not a stored flag. Submitted and
// unpaid needs payment; paid is in progress. A draft is a basket, not work.
function orderStatus(order: BusinessOrderSummary): { label: string; tone: StatusTone } {
  if (!order.submitted) return { label: 'Draft', tone: 'idle' }
  return ORDER_STATE_LABELS[order.state]
}

function formatDue(due_date: string | null, overdue: boolean): string {
  if (!due_date) return 'No due date'
  const date = new Date(due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  return overdue ? `Due ${date} — overdue` : `Due ${date}`
}

export default function BusinessesShow({ business, workspace_section, formation_tracker, tasks, orders, task_documents }: Props) {
  const basePath = useBasePath()

  const certificateMilestone = formation_tracker?.milestones.find((m) => m.key === 'certificate_issued')

  // "Needs you" is only ever the concrete customer records: an incomplete task
  // awaiting a response, or a submitted order that has not been paid. Nothing
  // is inferred from milestones, missing EINs or service counts.
  const pendingTasks = tasks.filter((t) => t.status === 'pending')
  const respondedTasks = tasks.filter((t) => t.status === 'responded')
  const completedTasks = tasks.filter((t) => t.status === 'completed')
  const unpaidOrders = orders.filter((o) => o.submitted && o.state === 'unpaid')
  const submittedOrders = orders.filter((o) => o.submitted)
  // A formation's progress is its own vertical track, so its order must not
  // also surface as a second generic Timeline item. Billing still lists it.
  const timelineOrders: BusinessOrderSummary[] = []
  const activeOrders = timelineOrders.filter((o) => o.state === 'ready' || o.state === 'in_progress')
  const historicalOrders = timelineOrders.filter((o) => o.state === 'completed' || o.state === 'refunded')
  const needsYouCount = pendingTasks.length + unpaidOrders.length
  const hasCorppyWork = formation_tracker !== null || submittedOrders.length > 0 || tasks.length > 0
  const hasTimelineContent = hasCorppyWork || activeOrders.length > 0 || historicalOrders.length > 0
  const hasAnyDocuments =
    (formation_tracker !== null && formation_tracker.documents_count > 0) ||
    task_documents.documents.length > 0 ||
    task_documents.templates.length > 0

  const trackSteps: TrackStep[] = (formation_tracker?.milestones ?? []).map((m, i, all) => ({
    key: m.key,
    label: MILESTONE_LABELS[m.key],
    detail: describeMilestone(m, formation_tracker!.entity, all.slice(i + 1).some((n) => n.done)).body,
    state: m.status === 'done' ? 'done' : m.status === 'in_progress' ? 'current' : 'upcoming',
  }))

  const taskPath = (id: string) => `${basePath}/businesses/${business.id}/business_tasks/${id}`
  const paymentPath = (id: string) => `${basePath}/orders/${id}/payment`

  return (
    <DashboardLayout
      title={business.name}
      breadcrumbs={[{ label: 'Businesses', href: 'businesses' }, { label: business.name }]}
      guide={(
        <BusinessSidebar
          businessId={business.id}
          activeSection={workspace_section}
          needsYouCount={needsYouCount}
        />
      )}
      tips={
        <Tip seed="business-next" title="What happens next">
          {hasCorppyWork
            ? 'Corppy keeps you posted as work moves. Anything we need from you appears at the top of the timeline.'
            : 'Start a company when you want Corppy to form it and handle the filing, registered agent and EIN.'}
        </Tip>
      }
    >
      <Page>
        <PageHeader
          title={business.name}
          actions={
            <BusinessMenuButton
              businessId={business.id}
              activeSection={workspace_section}
              needsYouCount={needsYouCount}
              className="lg:hidden"
            />
          }
        >
          {needsYouCount > 0 ? (
            <p className="text-sm text-foreground">
              {needsYouCount === 1 ? 'One thing needs your attention.' : `${needsYouCount} things need your attention.`}
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">
              {hasCorppyWork
                ? 'Your Corppy formation progress and orders appear below.'
                : 'No work has started yet. Start a company when you’re ready.'}
            </p>
          )}
        </PageHeader>

        {workspace_section === 'timeline' && (
          <div className="space-y-10">
            {!hasTimelineContent ? (
              <Empty
                icon={<Building2 className="size-5" />}
                title="No formation started yet"
              >
                Start a company and your formation filing, registered agent and document progress appears here.
              </Empty>
            ) : (
              <>
                {needsYouCount > 0 && (
                  <Section title="Needs your attention" headingId="actions">
                    <Rows>
                      {pendingTasks.map((task) => (
                        <Row
                          key={task.id}
                          icon={<ClipboardList className="size-4 text-muted-foreground" />}
                          title={task.type_name}
                          description={formatDue(task.due_date, task.overdue)}
                          href={taskPath(task.id)}
                        />
                      ))}
                      {unpaidOrders.map((order) => (
                        <Row
                          key={order.id}
                          icon={<CreditCard className="size-4 text-muted-foreground" />}
                          title={order.line_items.map((item) => item.description).join(' · ')}
                          description="Payment needed"
                          action={
                            <Button asChild size="sm">
                              <Link href={paymentPath(order.id)}>Pay {formatCurrency(order.total_cents)}</Link>
                            </Button>
                          }
                        />
                      ))}
                    </Rows>
                  </Section>
                )}

                {formation_tracker && (
                  <Section title="Progress" headingId="progress-heading">
                    {certificateMilestone?.done && (
                      <CertificateCard
                        milestone={certificateMilestone}
                        legalName={formation_tracker.legal_name}
                        jurisdictionCode={formation_tracker.entity.jurisdiction_code}
                      />
                    )}

                    <MilestoneTrack steps={trackSteps} />
                  </Section>
                )}

                {activeOrders.length > 0 && (
                  <Section title="Work in progress" headingId="work-heading">
                    <Rows>
                      {activeOrders.map((order) => {
                        const status = orderStatus(order)
                        return (
                          <Row
                            key={order.id}
                            icon={<Package className="size-4 text-muted-foreground" />}
                            title={order.line_items.map((item) => item.description).join(' · ')}
                            description={formatCurrency(order.total_cents)}
                            meta={<ListStatus label={status.label} tone={status.tone} />}
                            href={paymentPath(order.id)}
                          />
                        )
                      })}
                    </Rows>
                  </Section>
                )}

                {historicalOrders.length > 0 && (
                  <Section title="Order history" headingId="order-history-heading">
                    <Rows>
                      {historicalOrders.map((order) => {
                        const status = orderStatus(order)
                        return (
                          <Row
                            key={order.id}
                            muted
                            icon={<Package className="size-4 text-muted-foreground" />}
                            title={order.line_items.map((item) => item.description).join(' · ')}
                            description={formatCurrency(order.total_cents)}
                            meta={<ListStatus label={status.label} tone={status.tone} />}
                            href={paymentPath(order.id)}
                          />
                        )
                      })}
                    </Rows>
                  </Section>
                )}

                {respondedTasks.length > 0 && (
                  <Section title="With Corppy" headingId="waiting-heading">
                    <Rows>
                      {respondedTasks.map((task) => (
                        <Row
                          key={task.id}
                          muted
                          icon={<ClipboardList className="size-4 text-muted-foreground" />}
                          title={task.type_name}
                          description="Response received — we're reviewing it."
                          href={taskPath(task.id)}
                        />
                      ))}
                    </Rows>
                  </Section>
                )}

                {completedTasks.length > 0 && (
                  <Section title="Completed" headingId="completed-heading">
                    <Rows>
                      {completedTasks.map((task) => (
                        <Row
                          key={task.id}
                          muted
                          icon={<ClipboardList className="size-4 text-muted-foreground" />}
                          title={task.type_name}
                          meta={<ListStatus label="Complete" tone="positive" />}
                          href={taskPath(task.id)}
                        />
                      ))}
                    </Rows>
                  </Section>
                )}
              </>
            )}
          </div>
        )}

        {workspace_section === 'documents' && (
          <div className="space-y-10">
            {!hasAnyDocuments ? (
              <Empty icon={<FileText className="size-5" />} title="No documents yet">
                Documents appear here as Corppy files and your tasks gather paperwork.
              </Empty>
            ) : (
              <>
                {formation_tracker && formation_tracker.documents.length > 0 && (
                  <Section title="Formation" headingId="formation-documents-heading">
                    <Rows>
                      {formation_tracker.documents.map((doc) => (
                        <Row
                          key={doc.id}
                          icon={<FileText className="size-4 text-muted-foreground" />}
                          title={doc.name}
                          action={
                            <Button asChild variant="ghost" size="icon" className="h-7 w-7" aria-label={`Download ${doc.name}`}>
                              <a href={doc.file_url} target="_blank" rel="noopener noreferrer">
                                <Download className="size-3.5" />
                              </a>
                            </Button>
                          }
                        />
                      ))}
                    </Rows>
                  </Section>
                )}

                {(task_documents.documents.length > 0 || task_documents.templates.length > 0) && (
                  <Section title="Task documents" headingId="task-documents-heading">
                    <Rows>
                      {task_documents.documents.map((doc) => (
                        <Row
                          key={doc.id}
                          icon={<FileText className="size-4 text-muted-foreground" />}
                          title={doc.name}
                          description={doc.task_name}
                          action={
                            doc.file_url ? (
                              <Button asChild variant="ghost" size="icon" className="h-7 w-7" aria-label={`Download ${doc.name}`}>
                                <a href={doc.file_url} target="_blank" rel="noopener noreferrer">
                                  <Download className="size-3.5" />
                                </a>
                              </Button>
                            ) : undefined
                          }
                        />
                      ))}
                      {task_documents.templates.map((template) => (
                        <Row
                          key={template.id}
                          icon={<FileText className="size-4 text-muted-foreground" />}
                          title={template.name}
                          description={`${template.task_name} — template`}
                          action={
                            template.file_url ? (
                              <Button asChild variant="ghost" size="icon" className="h-7 w-7" aria-label={`Download ${template.name}`}>
                                <a href={template.file_url} target="_blank" rel="noopener noreferrer">
                                  <Download className="size-3.5" />
                                </a>
                              </Button>
                            ) : undefined
                          }
                        />
                      ))}
                    </Rows>
                  </Section>
                )}
              </>
            )}
          </div>
        )}

        {workspace_section === 'billing' && (
          <div className="space-y-10">
            {orders.length === 0 ? (
              <Empty icon={<CreditCard className="size-5" />} title="No orders yet">
                Orders appear here once you check out a formation.
              </Empty>
            ) : (
              <Section title="Orders" headingId="orders-heading">
                <Rows>
                  {orders.map((order) => {
                    const status = orderStatus(order)
                    const isPayable = order.submitted && !order.paid
                    return (
                      <Row
                        key={order.id}
                        icon={<CreditCard className="size-4 text-muted-foreground" />}
                        title={order.line_items.map((item) => item.description).join(' · ')}
                        description={formatCurrency(order.total_cents)}
                        meta={
                          <ListStatus label={status.label} tone={status.tone} />
                        }
                        href={order.submitted ? paymentPath(order.id) : undefined}
                        action={
                          isPayable ? (
                            <Button asChild size="sm">
                              <Link href={paymentPath(order.id)}>Pay</Link>
                            </Button>
                          ) : undefined
                        }
                      />
                    )
                  })}
                </Rows>
              </Section>
            )}
          </div>
        )}
      </Page>
    </DashboardLayout>
  )
}
